import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { toast } from 'sonner';
import {
  Rocket, RefreshCw, History, Tag, AlertCircle, CheckCircle2,
  Server, GitBranch, Terminal, ShieldAlert
} from 'lucide-react';

const PlatformManagement = () => {
  const { user } = useContext(AuthContext);
  const [status, setStatus] = useState(null);
  const [logLines, setLogLines] = useState([]);
  const [suggest, setSuggest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedTag, setSelectedTag] = useState('');
  const [releaseTag, setReleaseTag] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/platform/status`);
      setStatus(r.data);
      return r.data;
    } catch (e) {
      if (e?.response?.status !== 503) toast.error('Status nicht abrufbar');
      return null;
    }
  }, []);

  const fetchLog = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/platform/log?lines=60`);
      setLogLines(r.data.lines || []);
      return r.data.job;
    } catch { return null; }
  }, []);

  const fetchSuggest = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/platform/release/suggest`);
      setSuggest(r.data);
      setReleaseTag(r.data.suggestion_patch);
      setReleaseNotes((r.data.changes || []).join('\n'));
    } catch { /* Vorschlag optional */ }
  }, []);

  useEffect(() => {
    if (user?.role === 'superadmin') {
      fetchStatus(); fetchLog(); fetchSuggest();
    }
  }, [user, fetchStatus, fetchLog, fetchSuggest]);

  // Waehrend ein Job laeuft: alle 4s Log + Status pollen
  useEffect(() => {
    const running = status?.job?.running;
    if (running && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const job = await fetchLog();
        if (job && !job.running) {
          clearInterval(pollRef.current); pollRef.current = null;
          setBusy(false);
          await fetchStatus(); await fetchSuggest();
          job.success ? toast.success(`Fertig: ${job.message}`)
                      : toast.error(`Fehlgeschlagen: ${job.message}`);
        }
      }, 4000);
    }
    return () => { if (!status?.job?.running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [status, fetchLog, fetchStatus, fetchSuggest]);

  const startJob = async (kind, target) => {
    const label = kind === 'update' ? 'Update' : 'Rollback';
    if (!window.confirm(`${label} auf "${target}" wirklich starten?\n\nEmpfehlung: Vorher DB-Backup erstellen.\nBei fehlgeschlagenem Health-Check erfolgt ein automatischer Rollback.`)) return;
    try {
      setBusy(true);
      await axios.post(`${API}/platform/${kind}`, { target });
      toast.info(`${label} gestartet ...`);
      await fetchStatus();
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail || `${label} konnte nicht gestartet werden`);
    }
  };

  const createRelease = async () => {
    try {
      const r = await axios.post(`${API}/platform/release/create`, {
        tag: releaseTag, title: releaseTag, notes: releaseNotes
      });
      toast.success(`Release ${r.data.tag} erstellt`);
      await fetchStatus(); await fetchSuggest();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Release fehlgeschlagen');
    }
  };

  if (!user || user.role !== 'superadmin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800">Zugriff verweigert</h2>
          <p className="text-gray-600 mt-2">Nur SuperAdmin kann diese Seite aufrufen.</p>
        </div>
      </div>
    );
  }

  const running = status?.job?.running;

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-700 to-slate-900 rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-center gap-3">
          <Rocket className="w-10 h-10 text-white" />
          <div>
            <h1 className="text-3xl font-bold text-white">Plattform-Management</h1>
            <p className="text-slate-300 mt-1">Updates, Rollbacks und Releases — getrennt von Daten & Content</p>
          </div>
        </div>
      </div>

      {running && (
        <div className="mb-6 bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-center gap-3">
          <RefreshCw className="w-6 h-6 text-amber-600 animate-spin" />
          <div>
            <p className="font-semibold text-amber-900">{status.job.kind === 'update' ? 'Update' : 'Rollback'} läuft ({status.job.target}) ...</p>
            <p className="text-sm text-amber-800">Die Seite kann kurz nicht erreichbar sein, wenn das Backend neu startet — einfach warten.</p>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Status-Karte */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2"><Server className="w-5 h-5" /> Systemstatus</h2>
          {status ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Laufende Version:</span><span className="font-mono font-semibold">{status.version}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Commit:</span><span className="font-mono">{status.commit}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Hinter main:</span>
                <span className={status.commits_behind_main > 0 ? 'font-semibold text-amber-600' : 'font-semibold text-green-600'}>
                  {status.commits_behind_main > 0 ? `${status.commits_behind_main} Commit(s)` : 'aktuell ✓'}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Letzter guter Stand:</span><span className="font-mono">{status.last_good || '—'}</span></div>
              <div className="pt-2 border-t">
                {status.containers.map(c => (
                  <div key={c.name} className="flex justify-between">
                    <span className="text-gray-600">{c.name}:</span>
                    <span className={c.status?.includes('healthy') ? 'text-green-600 font-semibold' : 'text-amber-600 font-semibold'}>{c.status}</span>
                  </div>
                ))}
              </div>
              {!status.github_token_set && (
                <p className="text-xs text-red-600 flex items-center gap-1 pt-2"><ShieldAlert className="w-4 h-4" /> GITHUB_TOKEN fehlt in .env — Updates/Releases vom Dashboard nicht möglich</p>
              )}
            </div>
          ) : <p className="text-gray-500 text-sm">Lade ...</p>}
          <button onClick={() => { fetchStatus(); fetchLog(); }} className="mt-4 text-sm text-blue-600 hover:underline flex items-center gap-1"><RefreshCw className="w-4 h-4" /> Aktualisieren</button>
        </div>

        {/* Update / Rollback */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2"><GitBranch className="w-5 h-5" /> Update & Rollback</h2>
          <button disabled={busy || running} onClick={() => startJob('update', 'origin/main')}
            className="w-full mb-3 bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center gap-2">
            <Rocket className="w-5 h-5" /> Update auf neuesten Stand (main)
          </button>
          <div className="flex gap-2 mb-3">
            <select value={selectedTag} onChange={e => setSelectedTag(e.target.value)}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono">
              <option value="">Version wählen ...</option>
              {(status?.tags || []).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <button disabled={!selectedTag || busy || running} onClick={() => startJob('rollback', selectedTag)}
              className="bg-slate-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-slate-700 disabled:bg-gray-400 flex items-center gap-2">
              <History className="w-4 h-4" /> Wechseln
            </button>
          </div>
          <button disabled={busy || running || !status?.last_good} onClick={() => startJob('rollback', 'last-good')}
            className="w-full bg-amber-500 text-white py-2 px-4 rounded-lg font-semibold hover:bg-amber-600 disabled:bg-gray-400 flex items-center justify-center gap-2">
            <History className="w-5 h-5" /> Not-Rollback: letzter guter Stand
          </button>
          <p className="text-xs text-gray-500 mt-3">Vor jedem Update wird der laufende Stand automatisch gesichert. Schlägt der Health-Check fehl, erfolgt ein automatischer Rollback.</p>
        </div>
      </div>

      {/* Release-Vorschlag */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2"><Tag className="w-5 h-5" /> Neues Release (Versionsvorschlag automatisch)</h2>
        {suggest ? (
          <>
            <div className="grid md:grid-cols-3 gap-3 mb-4 text-sm">
              <div className="bg-gray-50 rounded-lg p-3"><span className="text-gray-600 block">Letztes Release</span><span className="font-mono font-semibold">{suggest.latest_tag}</span></div>
              <div className="bg-gray-50 rounded-lg p-3"><span className="text-gray-600 block">Neue Commits seither</span><span className="font-semibold">{suggest.commits_since}</span></div>
              <div className="bg-gray-50 rounded-lg p-3"><span className="text-gray-600 block">Vorschläge</span>
                <button onClick={() => setReleaseTag(suggest.suggestion_patch)} className={`font-mono mr-2 px-2 py-0.5 rounded ${releaseTag === suggest.suggestion_patch ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}>{suggest.suggestion_patch}</button>
                <button onClick={() => setReleaseTag(suggest.suggestion_minor)} className={`font-mono px-2 py-0.5 rounded ${releaseTag === suggest.suggestion_minor ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}>{suggest.suggestion_minor}</button>
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Release-Notes (automatisch aus Commits, editierbar)</label>
              <textarea value={releaseNotes} onChange={e => setReleaseNotes(e.target.value)} rows={4}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
            </div>
            <div className="flex gap-3 items-center">
              <input value={releaseTag} onChange={e => setReleaseTag(e.target.value)} placeholder="vX.Y.Z"
                className="border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm w-32" />
              <button disabled={suggest.commits_since === 0 || !suggest.github_token_set || busy || running} onClick={createRelease}
                className="bg-green-600 text-white px-5 py-2 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" /> Release auf GitHub erstellen
              </button>
              {suggest.commits_since === 0 && <span className="text-sm text-gray-500">Keine neuen Commits seit {suggest.latest_tag}</span>}
            </div>
            {(suggest.changes || []).length > 0 && (
              <div className="mt-4 text-xs text-gray-600">
                <span className="font-semibold">Änderungen seit {suggest.latest_tag}:</span>
                <ul className="list-disc list-inside">{suggest.changes.map((c, i) => <li key={i}>{c}</li>)}</ul>
              </div>
            )}
          </>
        ) : <p className="text-gray-500 text-sm">Lade Vorschlag ...</p>}
      </div>

      {/* Log */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2"><Terminal className="w-5 h-5" /> Update-Log</h2>
        <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-xs overflow-x-auto max-h-80 overflow-y-auto">
          {logLines.length ? logLines.join('\n') : '(noch keine Einträge)'}
        </pre>
      </div>
    </div>
  );
};

export default PlatformManagement;
